import React, { useState, useEffect } from "react";
import { ShoppingBag, Menu, X, Leaf } from "lucide-react";

export const Navbar = ({ activeTab, setActiveTab, cartCount, toggleCart }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navItems = [
    { label: "Home", key: "home" },
    { label: "Story & Craft", key: "story" },
    { label: "Shop", key: "shop" },
    { label: "Care Journal", key: "care" },
    { label: "Contact", key: "contact" }
  ];

  const handleNavClick = (key) => {
    setActiveTab(key);
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <nav
      data-testid="main-navigation"
      className={`fixed top-0 left-0 w-full z-40 transition-all duration-500 h-20 flex items-center ${
        isScrolled ? "glass-nav-scrolled text-[#F7F5F0] py-3 shadow-md" : "glass-nav text-[#2B3A2F] py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full flex justify-between items-center">
        {/* Brand Logo */}
        <div
          data-testid="navbar-logo"
          onClick={() => handleNavClick("home")}
          className="text-3xl font-light tracking-wide cursor-pointer font-cormorant flex items-center gap-2 select-none"
        >
          <Leaf className="w-6 h-6 text-[#C47D65]" />
          Kalorence
        </div>

        {/* Desktop Links */}
        <div className="hidden md:flex items-center space-x-10 font-outfit text-sm tracking-wider uppercase font-light">
          {navItems.map((item) => (
            <button
              key={item.key}
              data-testid={`nav-link-${item.key}`}
              onClick={() => handleNavClick(item.key)}
              className={`hover:text-[#C47D65] transition-colors duration-300 relative py-1 ${
                activeTab === item.key
                  ? "text-[#C47D65] font-medium"
                  : ""
              }`}
            >
              {item.label}
              {activeTab === item.key && (
                <span className="absolute bottom-0 left-0 w-full h-[1px] bg-[#C47D65] transition-all duration-300"></span>
              )}
            </button>
          ))}
        </div>

        {/* Action Elements */}
        <div className="flex items-center space-x-6">
          {/* Cart Icon Toggle */}
          <button
            data-testid="navbar-cart-toggle-button"
            onClick={toggleCart}
            className="relative p-2 rounded-full hover:bg-black/5 dark:hover:bg-white/5 transition-colors duration-300"
            aria-label="Toggle Shopping Cart"
          >
            <ShoppingBag className="w-5 h-5" />
            {cartCount > 0 && (
              <span
                data-testid="navbar-cart-count"
                className="absolute -top-1 -right-1 bg-[#C47D65] text-[#F7F5F0] text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center font-outfit"
              >
                {cartCount}
              </span>
            )}
          </button>

          {/* Mobile Menu Button */}
          <button
            data-testid="navbar-mobile-menu-button"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 rounded-full hover:bg-black/5 transition-colors duration-300"
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile Drawer Overlay */}
      {mobileMenuOpen && (
        <div
          data-testid="mobile-navigation-overlay"
          className="md:hidden fixed top-20 left-0 w-full bg-[#2B3A2F] text-[#F7F5F0] border-t border-white/10 flex flex-col p-8 space-y-6 font-outfit uppercase tracking-widest text-sm font-light z-50 shadow-xl"
        >
          {navItems.map((item) => (
            <button
              key={item.key}
              data-testid={`mobile-nav-link-${item.key}`}
              onClick={() => handleNavClick(item.key)}
              className={`text-left hover:text-[#C47D65] transition-colors duration-300 py-2 border-b border-white/5 ${
                activeTab === item.key ? "text-[#C47D65] font-semibold pl-2" : ""
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </nav>
  );
};
